import { FC, useMemo } from "react";
import { Box, Card } from "@mui/material";
import { AxisLabelsFormatterContextObject } from "highcharts";
import { kubernetesClustersTableColumns } from "../clusters-list/kubernetesClustersTableColumns";
import { kubernetesClustersHighchartsDataGenerator } from "../../../../utils/helpers/kubernetesClustersChartDataGenerator";
import { KubernetesCluster } from "@/services/cloudchipr.api";
import { moneyFormatter } from "@/utils/numeral/moneyFormatter";
import { DataGrid } from "@/storybook/data-grid/DataGrid";
import { MultiTypeHighcharts } from "@/storybook/c8r-highcharts/MultiTypeHighcharts";
import { getHighChartsColumnDataLabels } from "@/storybook/c8r-highcharts/common/utils/helpers/getHighChartsColumnDataLabels";
import { HighchartsProvider } from "@/storybook/c8r-highcharts/common/components/HighchartsProvider";
import { getAccountsBarViewChartTooltipValueGetter } from "@/components/pages/accounts-group/components/accounts/accounts-bar/utils/helpers/getAccountsBarViewChartTooltipValueGetter";

interface KubernetesClustersBarViewProps {
  clusters: KubernetesCluster[] | null;
}

export const KubernetesClustersBarView: FC<KubernetesClustersBarViewProps> = ({
  clusters,
}) => {
  const chartData = useMemo(() => {
    return kubernetesClustersHighchartsDataGenerator(clusters);
  }, [clusters]);

  if (!clusters) {
    return null;
  }

  return (
    <Card variant="outlined" sx={{ px: 3, pb: 3 }}>
      <Box mt={3} mb={4}>
        <HighchartsProvider>
          <MultiTypeHighcharts
            showLegends
            showDataLabels
            loading={false}
            chartType="column"
            chartData={chartData}
            tooltipOptions={tooltipOptions}
            noDataMessage="No data available"
            columnDataLabels={columnDataLabels}
            yAxisLabelsFormatter={yAxisFormatter}
            tooltipMarkupGenerator={getAccountsBarViewChartTooltipValueGetter}
          />
        </HighchartsProvider>
      </Box>

      <Box
        border="1px solid"
        borderColor="grey.300"
        borderRadius={2}
        position="relative"
      >
        <DataGrid
          styles={styles}
          data={clusters}
          columns={kubernetesClustersTableColumns}
        />
      </Box>
    </Card>
  );
};

const columnDataLabels = getHighChartsColumnDataLabels(
  "column",
  undefined,
  "black",
);

const tooltipOptions = {
  shared: true,
};

const styles = {
  tableHeaderRow: { "& th": { py: 2 }, "& th:first-of-type": { pl: 2 } },
  tableContainer: {
    "& td:first-of-type": { pl: 2 },
  },
};

const yAxisFormatter = (params: AxisLabelsFormatterContextObject) => {
  return `${moneyFormatter(+params.value)}`;
};
